import React, { useMemo } from "react";
import { Clock } from "lucide-react";
import { Modal } from "../../../../ui/modal/modal.tsx";
import { Button } from "../../../../ui/button/button.tsx";
import type { TimesheetSheetMeta } from "../../hooks/use-timesheet-entries.ts";
import {
  getTimesheetExtensionCopy,
  getTimesheetExtensionScenario,
} from "../../utils/timesheet-extension-copy.ts";
import styles from "./extend-timesheet-modal.module.css";

type Props = {
  open: boolean;
  meta: TimesheetSheetMeta;
  workDate: Date;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
};

export function ExtendTimesheetModal({
  open,
  meta,
  workDate,
  onClose,
  onConfirm,
  isSubmitting = false,
}: Props) {
  const scenario = useMemo(() => getTimesheetExtensionScenario(meta), [meta]);
  const copy = useMemo(
    () =>
      getTimesheetExtensionCopy(scenario, {
        workDate,
        attemptsRemaining: meta.attemptsRemaining,
      }),
    [scenario, workDate, meta.attemptsRemaining],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={copy.title}
      size="md"
      footer={
        <div className={styles.footer}>
          <Button
            variant="light-grey"
            onClick={onClose}
            disabled={isSubmitting}
          >
            No, Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isSubmitting} variant="accent">
            {isSubmitting ? "Submitting..." : "Yes, Continue"}
          </Button>
        </div>
      }
    >
      <div className={styles.container}>
        <div className={styles.iconWrapper}>
          <Clock size={32} aria-hidden />
        </div>
        <div className={styles.content}>
          <p className={styles.message}>
            <span>{copy.messageLead}</span>
            <br />
            <span className={styles.messageDate}>
              {copy.messageDate}
              {copy.messagePunctuation}
            </span>
          </p>
          {copy.subtext ? (
            <p className={styles.subtext}>{copy.subtext}</p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
